// LooksManagePosts.js

import React, { useState, useEffect, useContext } from 'react';
import { Link }                 from 'react-router-dom';
import { doc, updateDoc }       from 'firebase/firestore';
import { db }                   from '../utils/firebaseConfig';
import './ManagePosts.css';

import { LooksPostsContext }    from '../contexts/LooksPostsContext';

// If you use React-Query for the home page feed:
import { useQueryClient }       from 'react-query';

const LooksManagePosts = () => {
  const {
    posts: globalPosts,
    deletePostById,
    updateSinglePost,
    fetchMorePosts,
    loading,
    hasMore
  } = useContext(LooksPostsContext);

  const queryClient = useQueryClient();
  const [posts, setPosts] = useState([]);

  /* ─────────────  keep local list in sync  ───────────── */
  useEffect(() => setPosts(globalPosts.filter(p => !p.deleted)), [globalPosts]);

  /* ─────────────  infinite scroll  ───────────── */
  useEffect(() => {
    const onScroll = () => {
      if (
        window.innerHeight + window.scrollY >= document.body.scrollHeight - 200 &&
        !loading && hasMore
      ) fetchMorePosts();
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [loading, hasMore, fetchMorePosts]);

  /* ─────────────  handlers  ───────────── */
  const handleDelete = async id => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      await deletePostById(id);          // context takes care of local state
    }
  };

  const handleShowOnHome = async (post, showOnHomePage) => {
    const ref = doc(db, 'looksPosts', post.id);
    await updateDoc(ref, { showOnHomePage });
    updateSinglePost({ ...post, showOnHomePage });
    queryClient.invalidateQueries('homePagePosts');
  };

  const handleShowInDiscover = async (post, showInDiscoverBox) => {
    const ref = doc(db, 'looksPosts', post.id);
    await updateDoc(ref, { showInDiscoverBox });
    updateSinglePost({ ...post, showInDiscoverBox });
    // Add an invalidateQueries call here if Discover feed is cached
  };

  /* ─────────────  render  ───────────── */
  return (
    <div>
      <h2>Manage Posts</h2>
      <div>Total Posts: {posts.length}</div>

      <ul className="post-list">
        {posts.map(post => (
          <li key={post.id} className="post-item">
            {post.imageUrl && (
              <img
                src={post.imageUrl}
                alt={post.title || 'Post'}
                className="post-thumbnail"
              />
            )}

            <Link to={`/looksposts/${post.id}`} className="post-title">
              {post.title}
            </Link>

            <div>
              <label>
                <input
                  type="checkbox"
                  checked={post.showOnHomePage || false}
                  onChange={e => handleShowOnHome(post, e.target.checked)}
                />
                Show on Home Page
              </label>
            </div>

            <div>
              <label>
                <input
                  type="checkbox"
                  checked={post.showInDiscoverBox || false}
                  onChange={e => handleShowInDiscover(post, e.target.checked)}
                />
                Show in Discover Posts Box
              </label>
            </div>

            <button
              onClick={() => handleDelete(post.id)}
              className="button-delete"
            >
              Delete
            </button>

            {/* extra meta */}
            <div className="info-row">
              <div>
                <strong>Post Summary:</strong>{' '}
                {post.summary ||
                  (post.description.length > 100
                    ? `${post.description.substring(0, 100)}…`
                    : post.description)}
              </div>
              <div><strong>Name:</strong> {post.userName || post.author}</div>
              {post.bio && (
                <div>
                  <strong>Brief Bio:</strong> {post.bio}
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>

      {loading && <p>Loading more posts…</p>}
    </div>
  );
};

export default LooksManagePosts;
